import React, { Fragment, useEffect, useState } from 'react';
import { styled } from '@linaria/react';
import { css } from '@linaria/core';
import { useAtom } from 'jotai';
import useDeepCompareEffect from 'use-deep-compare-effect';
import useTranslation from 'next-translate/useTranslation';
import {
  Radio,
  RadioGroup,
  ButtonGroup,
  Button,
  breakpoints,
  Text,
  colors,
} from '@spaceshipapp/cambridge';
import { Service } from '../../types/common';
import { getQuickestService, getCheapestService, getArrivalDate } from './utils';
import Formatter from '../../utils/formatter';
import Currency from '../../utils/currency';
import { serviceAtom } from '../../contexts/shipment';
import quotationAtom, { prohibitedCouriersAtom } from '../../contexts/quotation';
import { getTrans } from '../../utils/helpers';
import * as analytics from '../../analytics/quotation';

enum TabId {
  SHORT = 'short',
  FULL = 'full',
}

interface ServiceListProps {
  isOpen?: boolean;
}

const ServiceList: React.FC<ServiceListProps> = ({ isOpen = false }) => {
  const { t, lang } = useTranslation('common');
  const [activeTab, setActiveTab] = useState(TabId.SHORT);
  const [options] = useAtom(quotationAtom);
  const [service, setService] = useAtom(serviceAtom);
  const [prohibitedCouriers] = useAtom(prohibitedCouriersAtom);

  const filteredOptions = options.filter((option) => !prohibitedCouriers.includes(option.carrier));

  const quickestService = getQuickestService(filteredOptions);
  const cheapestService = getCheapestService(filteredOptions);

  useEffect(() => {
    if (isOpen) {
      analytics.sendServiceOptionsViewed(activeTab);
    }
  }, [isOpen, activeTab]);

  useEffect(() => {
    if (service) {
      analytics.sendServiceChanged(service, filteredOptions);
    }
  }, [service]);

  useDeepCompareEffect(() => {
    // set cheapest service when options changed. if no cheapest service available, set quickest service
    setActiveTab(TabId.SHORT);
    setService(cheapestService?.slug || quickestService?.slug || '');
  }, [filteredOptions]);

  const renderDeliveryDays = (service: Service) => {
    if (!service.maxDeliveryDays) return null;

    const date = Formatter.date(
      getArrivalDate(service.maxDeliveryDays),
      t(`Datetime.dateFormatWithWeek`),
      lang,
    );
    return t('ServiceSelection.arrivesOnDate', { date });
  };

  const handleSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.value;
    setService(selected);
  };

  const handleChangeTab = (e: React.ChangeEvent<HTMLInputElement>) => {
    setActiveTab(e.target.value as TabId);
  };

  return (
    <Fragment>
      <RadioGroup value={activeTab} onChange={handleChangeTab}>
        <ButtonGroup size="sm">
          <Button
            as={Radio}
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            type="radio"
            variant={activeTab === TabId.SHORT ? `primary` : `default`}
            className={btnStyle}
            name="activeTab"
            value={TabId.SHORT}
            data-tracking-label={t('commonEn:ServiceSelection.shortTab')}
          >
            {t('ServiceSelection.shortTab')}
          </Button>
          <Button
            as={Radio}
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            type="radio"
            variant={activeTab === TabId.FULL ? `primary` : `default`}
            className={btnStyle}
            name="activeTab"
            value={TabId.FULL}
            data-tracking-label={t('commonEn:ServiceSelection.fullTab')}
          >
            {t('ServiceSelection.fullTab')}
          </Button>
        </ButtonGroup>
      </RadioGroup>
      <Wrapper>
        {activeTab === TabId.SHORT && (
          <List>
            <RadioGroup name="service" value={service} onChange={handleSelect}>
              {cheapestService && (
                <Radio
                  id={cheapestService.slug}
                  value={cheapestService.slug}
                  className={labelStyle}
                >
                  <Title>{t('ServiceSelection.bestValue')}</Title>
                  {!!cheapestService.maxDeliveryDays && (
                    <Text size="sm">{renderDeliveryDays(cheapestService)}</Text>
                  )}
                  {!!cheapestService.noTracking && (
                    <Text size="sm">{t('ServiceSelection.trackingNotAvailable')}</Text>
                  )}
                  <Text size="sm">{Currency.format(cheapestService.totalRate)}</Text>
                </Radio>
              )}
              {cheapestService && quickestService && cheapestService.slug !== quickestService.slug && (
                <Radio
                  id={quickestService.slug}
                  value={quickestService.slug}
                  className={labelStyle}
                >
                  <Title>{t('ServiceSelection.quickest')}</Title>
                  {!!quickestService.maxDeliveryDays && (
                    <Text size="sm">{renderDeliveryDays(quickestService)}</Text>
                  )}
                  {!!quickestService.noTracking && (
                    <Text size="sm">{t('ServiceSelection.trackingNotAvailable')}</Text>
                  )}
                  <Text size="sm">{Currency.format(quickestService.totalRate)}</Text>
                </Radio>
              )}
            </RadioGroup>
          </List>
        )}
        {activeTab === TabId.FULL && (
          <List>
            <RadioGroup name="service" value={service} onChange={handleSelect}>
              {filteredOptions.map((service) => {
                const { carrierName, slug, name, totalRate, noTracking, maxDeliveryDays } = service;
                return (
                  <Radio id={slug} value={slug} key={slug} className={labelStyle}>
                    <Text>
                      {getTrans(carrierName, lang)} {getTrans(name, lang)}
                    </Text>
                    {!!maxDeliveryDays && <Text size="sm">{renderDeliveryDays(service)}</Text>}
                    {!!noTracking && (
                      <Text size="sm">{t('ServiceSelection.trackingNotAvailable')}</Text>
                    )}
                    <Text size="sm">{Currency.format(totalRate)}</Text>
                  </Radio>
                );
              })}
            </RadioGroup>
          </List>
        )}
      </Wrapper>
    </Fragment>
  );
};

const btnStyle = css`
  flex-grow: 1;
  text-align: center;

  > span {
    /* hide fake radio */
    display: none;
  }
`;

const Wrapper = styled.div`
  flex: 1;
  height: auto;
  overflow: auto;
  margin-top: 1rem;

  @media (min-width: ${breakpoints.md}) {
    margin-top: 1.5rem;
  }
`;

const List = styled.div`
  display: flex;
  flex-direction: column;

  > *:not(:first-child) {
    margin-top: 1rem;
  }

  @media (min-width: ${breakpoints.md}) {
    > *:not(:first-child) {
      margin-top: 1.5rem;
    }
  }
`;

const labelStyle = css`
  display: flex;
  padding: 1em;
  border: 1px solid ${colors.border};
  flex-direction: row-reverse;

  > *:last-child {
    margin-left: 0em;
    margin-right: 0.5em;
  }

  @media (min-width: ${breakpoints.md}) {
    padding: 1.5em;
  }
`;

const Title = styled.p`
  font-size: 1rem;

  @media (min-width: ${breakpoints.md}) {
    font-size: 1.5rem;
  }
`;

export default ServiceList;
